import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Swords, Plus, Trash2, Lock, Unlock, Award, Users } from "lucide-react";

const DIVISIONS = ["Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Pro"];
const PHASES = [
  ["dink_wars_winner", "Dink Wars"],
  ["transition_wars_winner", "Transition Wars"],
  ["game_play_winner", "Game Play"],
];

function preview(p, aName, bName) {
  if (!p.dink_wars_winner || !p.transition_wars_winner || !p.game_play_winner) return null;
  const aPts = (p.dink_wars_winner === "A") + (p.transition_wars_winner === "A");
  const bPts = (p.dink_wars_winner === "B") + (p.transition_wars_winner === "B");
  const sweep = p.dink_wars_winner === p.transition_wars_winner && p.transition_wars_winner === p.game_play_winner;
  const winner = p.game_play_winner === "A" ? aName : bName;
  return { aPts, bPts, winner, bonus: sweep };
}

export default function BplMatchCenter() {
  const [division, setDivision] = useState("Intermediate");
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [results, setResults] = useState({}); // matchId -> phases
  const [newMatch, setNewMatch] = useState({ team_a: "", team_b: "", date: new Date().toISOString().slice(0, 10), venue: "indoor" });
  const [newTeam, setNewTeam] = useState({ name: "", player1: "", player2: "", player3: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [t, m] = await Promise.all([
      api.get("/bpl/teams", { params: { division } }),
      api.get("/bpl/matches", { params: { division } }),
    ]);
    setTeams(t.data); setMatches(m.data);
  }, [division]);

  useEffect(() => { load(); }, [load]);

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";

  const createTeam = async () => {
    if (!newTeam.name || !newTeam.player1 || !newTeam.player2) return toast.error("Team name + 2 players required");
    setBusy(true);
    try {
      await api.post("/bpl/teams", { ...newTeam, player3: newTeam.player3 || null, division });
      toast.success("Team created");
      setNewTeam({ name: "", player1: "", player2: "", player3: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const deleteTeam = async (id) => { await api.delete(`/bpl/teams/${id}`); toast.success("Team removed"); await load(); };

  const createMatch = async () => {
    if (!newMatch.team_a || !newMatch.team_b) return toast.error("Select both teams");
    if (newMatch.team_a === newMatch.team_b) return toast.error("Teams must differ");
    setBusy(true);
    try {
      await api.post("/bpl/matches", { ...newMatch, division });
      toast.success("Match created");
      setNewMatch({ ...newMatch, team_a: "", team_b: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setPhase = (mid, key, val) => setResults((r) => ({ ...r, [mid]: { ...r[mid], [key]: val } }));

  const submitResult = async (m) => {
    const p = results[m.id] || {};
    if (!p.dink_wars_winner || !p.transition_wars_winner || !p.game_play_winner)
      return toast.error("All 3 phases need a winner");
    setBusy(true);
    try {
      await api.put(`/bpl/matches/${m.id}/result`, p);
      toast.success("Match locked & scored");
      setResults((r) => { const n = { ...r }; delete n[m.id]; return n; });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const unlock = async (id) => { await api.post(`/bpl/matches/${id}/unlock`); toast.success("Unlocked for editing"); await load(); };
  const deleteMatch = async (id) => { await api.delete(`/bpl/matches/${id}`); toast.success("Match deleted"); await load(); };

  return (
    <div className="mt-8" data-testid="bpl-match-center">
      <h3 className="font-display font-bold text-2xl uppercase mb-1 flex items-center gap-2"><Swords className="w-6 h-6 text-pb-red" /> BPL Match Center</h3>
      <p className="text-sm text-zinc-500 mb-5">Authoritative league entry. Record 3 phases → auto-scored → locked. Team Points = skill · Match Wins = results · Bonus = full sweep.</p>

      {/* Division */}
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs uppercase tracking-widest text-zinc-500">Division:</span>
        {DIVISIONS.map((d) => (
          <button key={d} data-testid={`bpl-div-${d}`} onClick={() => setDivision(d)}
            className={`px-3 py-1.5 text-xs font-bold uppercase border transition-colors ${division === d ? "bg-pb-red text-black border-pb-red" : "border-pb-border text-zinc-500 hover:text-white"}`}>
            {d}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Create team */}
        <div className="bg-pb-surface border border-pb-border p-5">
          <div className="flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-pb-red" /><span className="font-display font-bold uppercase">New Team</span></div>
          <div className="space-y-2">
            <input data-testid="team-name" value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })} placeholder="Team name"
              className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm focus:outline-none focus:border-pb-red" />
            <input data-testid="team-p1" value={newTeam.player1} onChange={(e) => setNewTeam({ ...newTeam, player1: e.target.value })} placeholder="Player 1"
              className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm focus:outline-none focus:border-pb-red" />
            <input data-testid="team-p2" value={newTeam.player2} onChange={(e) => setNewTeam({ ...newTeam, player2: e.target.value })} placeholder="Player 2"
              className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm focus:outline-none focus:border-pb-red" />
            <input data-testid="team-p3" value={newTeam.player3} onChange={(e) => setNewTeam({ ...newTeam, player3: e.target.value })} placeholder="Substitute (optional)"
              className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm focus:outline-none focus:border-pb-red" />
            <button data-testid="create-team-btn" onClick={createTeam} disabled={busy}
              className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-2 text-sm hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">Add Team</button>
          </div>
          <div className="mt-4 space-y-1 max-h-40 overflow-y-auto">
            {teams.map((t) => (
              <div key={t.id} className="flex items-center justify-between text-sm border-b border-pb-border/40 py-1.5">
                <span><span className="font-semibold">{t.name}</span> <span className="text-xs text-zinc-500">{t.player1} / {t.player2}{t.player3 ? ` (+${t.player3})` : ""}</span></span>
                <button onClick={() => deleteTeam(t.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Create match */}
        <div className="lg:col-span-2 bg-pb-surface border border-pb-border p-5">
          <div className="flex items-center gap-2 mb-4"><Plus className="w-4 h-4 text-pb-red" /><span className="font-display font-bold uppercase">Create Match</span></div>
          <div className="grid sm:grid-cols-4 gap-3 items-end">
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Team A</label>
              <select data-testid="match-team-a" value={newMatch.team_a} onChange={(e) => setNewMatch({ ...newMatch, team_a: e.target.value })}
                className="w-full bg-pb-elevated border border-pb-border px-2 py-2 text-sm focus:outline-none focus:border-pb-red">
                <option value="">Select…</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Team B</label>
              <select data-testid="match-team-b" value={newMatch.team_b} onChange={(e) => setNewMatch({ ...newMatch, team_b: e.target.value })}
                className="w-full bg-pb-elevated border border-pb-border px-2 py-2 text-sm focus:outline-none focus:border-pb-red">
                <option value="">Select…</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Date</label>
              <input data-testid="match-date" type="date" value={newMatch.date} onChange={(e) => setNewMatch({ ...newMatch, date: e.target.value })}
                className="w-full bg-pb-elevated border border-pb-border px-2 py-2 text-sm focus:outline-none focus:border-pb-red" />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Venue</label>
              <select data-testid="match-venue" value={newMatch.venue} onChange={(e) => setNewMatch({ ...newMatch, venue: e.target.value })}
                className="w-full bg-pb-elevated border border-pb-border px-2 py-2 text-sm focus:outline-none focus:border-pb-red">
                <option value="indoor">Indoor</option>
                <option value="outdoor">Outdoor</option>
              </select>
            </div>
            <button data-testid="create-match-btn" onClick={createMatch} disabled={busy}
              className="bg-pb-red text-black font-bold uppercase tracking-widest py-2 text-sm hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">Create</button>
          </div>
        </div>
      </div>

      {/* Match list with fast result entry */}
      <div className="bg-pb-surface border border-pb-border">
        <div className="p-5 border-b border-pb-border"><span className="font-display font-bold text-lg uppercase">Matches ({matches.length})</span></div>
        <div className="divide-y divide-pb-border/50">
          {matches.length === 0 && <div className="p-5 text-sm text-zinc-600">No matches in this division yet.</div>}
          {matches.map((m) => {
            const locked = m.status === "Locked";
            const p = results[m.id] || {};
            const pv = preview(p, m.team_a_name, m.team_b_name);
            return (
              <div key={m.id} data-testid={`bpl-match-${m.id}`} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold text-sm">
                    <span className="text-xs text-zinc-500 font-mono mr-2">{m.date}</span>
                    {m.team_a_name} <span className="text-zinc-600">vs</span> {m.team_b_name}
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${locked ? "text-pb-cyan" : "text-amber-400"}`}>
                    {locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}{m.status}
                  </span>
                </div>

                {locked && m.scoring ? (
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="font-mono text-zinc-400">Team Pts: <span className="text-pb-red font-bold">{m.scoring.team_a_points}</span> – <span className="text-pb-red font-bold">{m.scoring.team_b_points}</span></span>
                    <span className="font-display font-bold text-pb-cyan">Match: {m.scoring.match_winner === m.team_a ? m.team_a_name : m.team_b_name}</span>
                    {m.scoring.bonus_awarded && <span className="flex items-center gap-1 text-amber-400 text-xs font-bold uppercase"><Award className="w-4 h-4" /> Bonus</span>}
                    <div className="ml-auto flex gap-2">
                      <button data-testid={`unlock-${m.id}`} onClick={() => unlock(m.id)} className="text-xs uppercase tracking-widest border border-pb-border px-3 py-1.5 hover:border-amber-400 hover:text-amber-400">Override</button>
                      <button data-testid={`del-match-${m.id}`} onClick={() => deleteMatch(m.id)} className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-red-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
                    {PHASES.map(([key, label]) => (
                      <div key={key}>
                        <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label} Winner</label>
                        <select data-testid={`phase-${key}-${m.id}`} value={p[key] || ""} onChange={(e) => setPhase(m.id, key, e.target.value)}
                          className="w-full bg-pb-elevated border border-pb-border px-2 py-2 text-sm focus:outline-none focus:border-pb-red">
                          <option value="">—</option>
                          <option value="A">{m.team_a_name}</option>
                          <option value="B">{m.team_b_name}</option>
                        </select>
                      </div>
                    ))}
                    <button data-testid={`submit-${m.id}`} onClick={() => submitResult(m)} disabled={busy || !pv}
                      className="bg-pb-red text-black font-bold uppercase tracking-widest py-2 px-4 text-sm hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">Submit & Lock</button>
                    {pv && (
                      <div className="sm:col-span-4 text-xs text-zinc-400 flex flex-wrap gap-3 pt-1">
                        <span>Team Pts: <span className="text-pb-red font-bold">{pv.aPts}</span>–<span className="text-pb-red font-bold">{pv.bPts}</span></span>
                        <span>Match Winner: <span className="text-pb-cyan font-bold">{pv.winner}</span></span>
                        {pv.bonus && <span className="text-amber-400 font-bold flex items-center gap-1"><Award className="w-3.5 h-3.5" /> Bonus (+1)</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
